import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { ArrowLeft, Sparkles, Copy, Check, ShieldCheck, MessageSquareText, Terminal } from "lucide-react";

// ---------------------------------------------------------------------------
// Conectar FinReport con Claude / ChatGPT (servidor MCP con OAuth de Clerk).
// Página estática de ayuda: no llama a la API. La URL del conector sale de
// REACT_APP_MCP_URL (default producción). El aislamiento por usuario lo
// garantiza el backend (backend/mcp_service/README.md).
// ---------------------------------------------------------------------------

const MCP_URL = process.env.REACT_APP_MCP_URL || "https://api.pflabs.es/mcp";

const PREGUNTAS = [
  "¿Qué empresas tengo en FinReport?",
  "Dame el balance y la cuenta de resultados de mi empresa de 2025.",
  "¿Cuál es el DFN/EBITDA y el plazo de cobro? ¿Ha mejorado respecto al año anterior?",
  "Explícame el flujo de caja de 2025: ¿por qué bajó la tesorería?",
  "¿Qué ajustes lleva el EBITDA normalizado?",
  "Resume los supuestos de la proyección y compárame los tres escenarios.",
];

function CopiarUrl() {
  const [copiado, setCopiado] = useState(false);
  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(MCP_URL);
      setCopiado(true);
      toast.success("URL copiada");
      setTimeout(() => setCopiado(false), 2000);
    } catch (e) {
      toast.error("No se pudo copiar. Selecciona la URL y cópiala a mano.");
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2">
      <code className="rounded bg-muted px-3 py-2 font-mono text-sm select-all" data-testid="mcp-url">
        {MCP_URL}
      </code>
      <Button variant="outline" size="sm" onClick={copiar} data-testid="mcp-url-copy">
        {copiado ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
        {copiado ? "Copiada" : "Copiar"}
      </Button>
    </div>
  );
}

function Pasos({ pasos }) {
  return (
    <ol className="list-decimal space-y-2 pl-5 text-sm">
      {pasos.map((p, i) => (
        <li key={i}>{p}</li>
      ))}
    </ol>
  );
}

export default function ConectarIAPage() {
  const navigate = useNavigate();

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/empresas")} data-testid="conectar-ia-back">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="page-title flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" />
            Conectar con ChatGPT y Claude
          </h1>
          <p className="text-muted-foreground mt-1">
            Pregunta a tu asistente de IA por tus empresas de FinReport sin abrir la web: balances,
            cuenta de resultados, ratios, flujos de caja, EBITDA ajustado y proyecciones. Solo lectura.
          </p>
        </div>
      </div>

      {/* Cómo funciona */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-primary" />
            Cómo funciona y qué ve la IA
          </CardTitle>
          <CardDescription>
            Al añadir el conector, ChatGPT o Claude te pedirán iniciar sesión con tu cuenta de FinReport y
            aceptar el permiso <Badge variant="secondary">Leer tus empresas de FinReport</Badge>. A partir de ahí
            el asistente consulta tus datos con tu propia identidad: ve exactamente tus empresas y las que te han
            compartido, nada más. Puedes revocar el acceso cuando quieras eliminando el conector.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm font-medium">URL del conector</p>
          <CopiarUrl />
          <Alert>
            <AlertDescription className="text-sm">
              Los datos que pidas se envían al proveedor de IA que uses (OpenAI o Anthropic) según sus condiciones.
              No uses el conector si tu empresa no lo permite.
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Claude */}
        <Card>
          <CardHeader>
            <CardTitle>Claude (web, escritorio y móvil)</CardTitle>
            <CardDescription>
              Planes Free, Pro y Max: lo añades tú. En Team/Enterprise lo añade un Owner y luego cada usuario lo activa.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Pasos
              pasos={[
                "En claude.ai abre Configuración → Conectores (Customize → Connectors).",
                "Pulsa «Añadir conector personalizado».",
                "Nombre: FinReport. URL: la de arriba. Deja Client ID y Client Secret vacíos.",
                "Pulsa Añadir y luego Conectar: inicia sesión en FinReport y acepta el permiso.",
                "En un chat nuevo, activa el conector FinReport en el menú de herramientas y pregunta.",
              ]}
            />
            <div className="rounded-md bg-muted p-3 text-xs space-y-1">
              <p className="flex items-center gap-1 font-medium">
                <Terminal className="h-3.5 w-3.5" /> Claude Code
              </p>
              <code className="block font-mono select-all">claude mcp add --transport http finreport {MCP_URL}</code>
            </div>
          </CardContent>
        </Card>

        {/* ChatGPT */}
        <Card>
          <CardHeader>
            <CardTitle>ChatGPT</CardTitle>
            <CardDescription>
              Requiere activar el modo desarrollador en Configuración → Seguridad e inicio de sesión (o Apps y
              conectores → Ajustes avanzados, según versión).
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Pasos
              pasos={[
                "Configuración → Conectores (Apps & Connectors) → Crear / Añadir.",
                "Nombre: FinReport. URL del servidor MCP: la de arriba. Autenticación: OAuth. Client ID vacío.",
                "Guarda y pulsa Conectar: inicia sesión en FinReport y acepta el permiso.",
                "En el chat elige el conector FinReport (o úsalo como fuente en Deep research) y pregunta.",
              ]}
            />
          </CardContent>
        </Card>
      </div>

      {/* Preguntas de ejemplo */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquareText className="h-5 w-5 text-primary" />
            Preguntas que funcionan bien
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="grid gap-2 md:grid-cols-2 text-sm">
            {PREGUNTAS.map((q) => (
              <li key={q} className="rounded-md border px-3 py-2 text-muted-foreground">
                “{q}”
              </li>
            ))}
          </ul>
          <p className="mt-4 text-xs text-muted-foreground">
            Si el asistente responde «vuelve a conectar», el acceso ha caducado o se revocó: elimina el conector y
            vuelve a añadirlo. Si dice que una empresa «no está entre tus empresas», es que no existe en FinReport o
            no la tienes ni propia ni compartida.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
